import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { DoseCalcModeActive, DoseLimitsMatrix, DurationUnit, OverdosePreventionLevel, PreparationType, RoundingMode, } from '../../database/schema.js';
import { queries } from '../../queries.js';
import { zql } from '../../schema.js';
import { itemsDetail, statusItems, valueItem } from '../../util/event-log-items.js';
import { pickActorFromUserActions, pickTimestampFromUserActions, titleCase } from '../../util/index.js';
import { Status } from '../../util/schemas.js';
import { assertCan } from '../../utils.js';
import { assertDoseUnitUnlocked } from './dose-unit-lock.js';
import { DeleteResourceSchema, EventLogResources, UpdateResourceStatusSchema, addEventLog, } from '../util.js';
import { AddDrugItemSchema, createDrugCatalogItem, createDrugItemCategories, createMedicationProfiles, replaceDrugItemCategories, replaceMedicationProfiles, updateDrugCatalogItem, } from './utils/index.js';
import { upsertCompoundMapping } from '../compounders/utils.js';
import { CompoundMappingType } from '../../database/schema.js';
export const ActiveSubstanceSchema = AddDrugItemSchema.omit({ catalogType: true }).extend({
    doseCalculationMethod: DoseCalcModeActive.nonoptional(),
    doseUnitId: z.uuidv7().nonempty(),
    doseLimits: DoseLimitsMatrix.nonoptional(),
    roundingMode: RoundingMode.nonoptional(),
    precision: z.number().gte(0).optional(),
    minIntervalValue: z.number().gte(0).optional(),
    minIntervalUnit: DurationUnit.optional(),
    overdosePreventionLevel: OverdosePreventionLevel.nonoptional(),
    hazardClassId: z.array(z.uuidv7()),
    hasSaltContent: z.boolean(),
    molarMassFreeBase: z.number().gte(0).optional(),
    molarMassSaltBase: z.number().gte(0).optional(),
    saltFactor: z.number().gte(0).optional(),
    additionalApprovalRequired: z.boolean().optional(),
    cytotoxic: z.boolean(),
    routeWarning: z.boolean(),
    productionNotes: z.string().optional(),
    externalInformationLink: z.url().or(z.literal('')).optional(),
    preparationType: PreparationType.optional(),
});
export const AddActiveSubstanceSchema = ActiveSubstanceSchema;
export const UpdateActiveSubstanceSchema = ActiveSubstanceSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
async function findById(tx, ctx, id) {
    return await tx.run(queries.drugCatalog.getActiveSubstanceById.fn({ args: id, ctx }));
}
async function createActiveSubstanceDetails(tx, args, userActions) {
    const newActiveSubstance = {
        doseCalcMode: args.doseCalculationMethod,
        dosePrecision: args.precision,
        doseUnitId: args.doseUnitId,
        drugCatalogItemId: args.drugItemId,
        doseLimits: args.doseLimits,
        overdosePreventionLevel: args.overdosePreventionLevel,
        roundingMode: args.roundingMode,
        hasSaltContent: args.hasSaltContent,
        minIntervalDurationUnit: args.minIntervalUnit,
        minIntervalValue: args.minIntervalValue,
        molarMassFreeBase: args.molarMassFreeBase,
        molarMassSaltBase: args.molarMassSaltBase,
        saltFactor: args.saltFactor,
        additionalApprovalRequired: args.additionalApprovalRequired,
        cytotoxic: args.cytotoxic,
        routeWarning: args.routeWarning,
        productionNotes: args.productionNotes,
        externalInformationLink: args.externalInformationLink,
        preparationType: args.preparationType,
        ...userActions,
    };
    const promises = [tx.mutate.activeSubstanceDetails.insert(newActiveSubstance)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newActiveSubstance);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.activeSubstanceDetails,
            resourceId: newActiveSubstance.drugCatalogItemId,
            action: 'create',
            patches: patches,
            performedBy: pickActorFromUserActions(userActions),
            performedAt: pickTimestampFromUserActions(userActions),
            status: 'OK',
            details: { id: newActiveSubstance.drugCatalogItemId, name: titleCase(args.name) },
        }));
    }
    await Promise.all(promises);
    return newActiveSubstance;
}
async function updateActiveSubstanceDetails(tx, prev, args, userActions) {
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const updatedActiveSubstance = {
        doseCalcMode: args.doseCalculationMethod,
        dosePrecision: args.precision,
        doseUnitId: args.doseUnitId,
        drugCatalogItemId: args.id,
        doseLimits: args.doseLimits,
        overdosePreventionLevel: args.overdosePreventionLevel,
        roundingMode: args.roundingMode,
        hasSaltContent: args.hasSaltContent,
        minIntervalDurationUnit: args.minIntervalUnit,
        minIntervalValue: args.minIntervalValue,
        molarMassFreeBase: args.molarMassFreeBase,
        molarMassSaltBase: args.molarMassSaltBase,
        saltFactor: args.saltFactor,
        additionalApprovalRequired: args.additionalApprovalRequired,
        cytotoxic: args.cytotoxic,
        routeWarning: args.routeWarning,
        productionNotes: args.productionNotes,
        externalInformationLink: args.externalInformationLink,
        preparationType: args.preparationType,
        updatedAt: timestamp,
        updatedBy: actor,
    };
    const promises = [tx.mutate.activeSubstanceDetails.update(updatedActiveSubstance)];
    if (tx.location === 'server') {
        const patches = createPatch(prev.activeSubstanceDetails, updatedActiveSubstance);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.activeSubstanceDetails,
            resourceId: updatedActiveSubstance.drugCatalogItemId,
            action: 'update',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            status: 'OK',
            details: { id: args.id, name: prev.name },
        }));
    }
    await Promise.all(promises);
    return updatedActiveSubstance;
}
/** Drug name and hazard class names for hazard class logs. */
async function hazardClassLogNames(tx, drugId, hazardClassIds) {
    const drug = await tx.run(zql.drugCatalogItems.where('id', '=', drugId).one());
    const hazardClasses = hazardClassIds.length ? await tx.run(zql.hazardClasses.where('id', 'IN', hazardClassIds)) : [];
    return {
        itemName: drug?.name,
        hazardClass: (id) => hazardClasses.find(row => row.id === id)?.name,
    };
}
async function createActiveSubstanceHazardClasses(tx, args, userActions) {
    const names = tx.location === 'server' ? await hazardClassLogNames(tx, args.substanceDetailId, args.hazardClassesIds) : null;
    const relations = args.hazardClassesIds.flatMap(hazardClassId => {
        const newRelation = {
            id: uuidv7(),
            hazardClassId,
            activeSubstanceDetailId: args.substanceDetailId,
            ...userActions,
        };
        const promises = [tx.mutate.activeSubstanceHazardClasses.insert(newRelation)];
        if (names) {
            const patches = createPatch({}, newRelation);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.activeSubstanceHazardClass,
                resourceId: newRelation.id,
                action: 'create',
                patches: patches,
                performedBy: pickActorFromUserActions(userActions),
                performedAt: pickTimestampFromUserActions(userActions),
                status: 'OK',
                details: {
                    itemName: names.itemName,
                    ...itemsDetail(valueItem('hazard_class', names.hazardClass(hazardClassId))),
                },
            }));
        }
        return promises;
    });
    await Promise.all(relations);
}
export async function replaceActiveSubstanceHazardClasses(tx, { drugItem, newHazardClassesIds }, userActions) {
    const currentHazardClasses = new Set(drugItem.activeSubstanceDetails?.hazardClasses?.map(item => item.hazardClassId));
    const newHazardClassesSet = new Set(newHazardClassesIds);
    const addedHazardClasses = newHazardClassesIds.filter(id => !currentHazardClasses.has(id));
    const deletedHazardClasses = drugItem.activeSubstanceDetails?.hazardClasses?.filter(item => !newHazardClassesSet.has(item.hazardClassId)) || [];
    const userActionsTimestamp = pickTimestampFromUserActions(userActions);
    const userActionsActor = pickActorFromUserActions(userActions);
    if (addedHazardClasses.length) {
        await createActiveSubstanceHazardClasses(tx, { hazardClassesIds: addedHazardClasses, substanceDetailId: drugItem.id }, { createdAt: userActionsTimestamp, createdBy: userActionsActor });
    }
    if (deletedHazardClasses.length) {
        const names = tx.location === 'server'
            ? await hazardClassLogNames(tx, drugItem.id, deletedHazardClasses.map(c => c.hazardClassId))
            : null;
        const relationsToMarkAsDeleted = deletedHazardClasses.flatMap(c => {
            const updatedRelation = {
                id: c.id,
                deletedAt: userActionsTimestamp,
                deletedBy: userActionsActor,
            };
            const promises = [tx.mutate.activeSubstanceHazardClasses.update(updatedRelation)];
            if (names) {
                const patches = createPatch({}, updatedRelation);
                promises.push(addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.activeSubstanceHazardClass,
                    resourceId: updatedRelation.id,
                    action: 'delete',
                    patches: patches,
                    performedBy: userActionsActor,
                    performedAt: userActionsTimestamp,
                    status: 'WARNING',
                    details: {
                        itemName: names.itemName,
                        ...itemsDetail(valueItem('hazard_class', names.hazardClass(c.hazardClassId))),
                    },
                }));
            }
            return promises;
        });
        await Promise.all(relationsToMarkAsDeleted);
    }
}
export const activeSubstances = {
    add: defineMutator(AddActiveSubstanceSchema, async ({ ctx, tx, args }) => {
        assertCan(ctx, 'catalogue.manage');
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const newDrugItem = await createDrugCatalogItem(tx, { ...args, catalogType: 'ACTIVE_SUBSTANCE' }, userActions);
        const newDetails = await createActiveSubstanceDetails(tx, { ...args, drugItemId: newDrugItem.id }, userActions);
        await createActiveSubstanceHazardClasses(tx, { hazardClassesIds: args.hazardClassId, substanceDetailId: newDetails.drugCatalogItemId }, userActions);
        await createDrugItemCategories(tx, { categories: args.categoriesIds, drugItemId: newDrugItem.id }, userActions);
        await createMedicationProfiles(tx, { medicationProfiles: args.medicationProfiles, drugItemId: newDrugItem.id }, userActions);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.DRUG,
            sourceId: newDrugItem.id,
            machineId: args.machineId,
            actorId: ctx.user.id,
            now,
        });
    }),
    update: defineMutator(UpdateActiveSubstanceSchema, async ({ ctx, tx, args }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await updateDrugCatalogItem(tx, prev, args, userActions);
        await assertDoseUnitUnlocked(tx, {
            substanceId: args.id,
            substanceName: prev.name,
            previousUnitId: prev.activeSubstanceDetails?.doseUnitId,
            nextUnitId: args.doseUnitId,
        });
        await updateActiveSubstanceDetails(tx, prev, args, userActions);
        await replaceActiveSubstanceHazardClasses(tx, { drugItem: prev, newHazardClassesIds: args.hazardClassId || [] }, userActions);
        await replaceDrugItemCategories(tx, { drugItem: prev, newDrugCategoriesIds: args.categoriesIds || [] }, userActions);
        await replaceMedicationProfiles(tx, { drugItem: prev, newMedicationProfiles: args.medicationProfiles || [] }, userActions);
        await upsertCompoundMapping(tx, {
            mappingType: CompoundMappingType.enum.DRUG,
            sourceId: args.id,
            machineId: args.machineId,
            actorId: ctx.user.id,
            now,
        });
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedDrugItem = {
            id: args.id,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.drugCatalogItems.update(updatedDrugItem);
        if (tx.location === 'server') {
            const patches = createPatch({ status: prev.status }, { status: args.status });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugCatalogItem,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedDrugItem.updatedBy,
                performedAt: updatedDrugItem.updatedAt,
                status: args.status === Status.enum.inactive ? 'WARNING' : 'INFO',
                details: { id: args.id, name: prev.name, ...itemsDetail(statusItems(prev.status, args.status)) },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, ctx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedDrug = {
            id: args.id,
            code: `${prev.code}_deleted_${now}`,
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        await tx.mutate.drugCatalogItems.update(updatedDrug);
        if (tx.location === 'server') {
            const patches = createPatch({ code: prev.code }, { code: updatedDrug.code });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugCatalogItem,
                resourceId: args.id,
                action: 'delete',
                patches: patches,
                performedBy: updatedDrug.deletedBy,
                performedAt: updatedDrug.deletedAt,
                status: 'WARNING',
                details: { id: args.id, name: prev.name },
            }, ctx);
        }
        // await Promise.all(
        //   prev.categories.flatMap(c => {
        //     const updatedRelation = {
        //       id: c.id,
        //       deletedAt: updatedSubstance.deletedAt,
        //       deletedBy: updatedSubstance.deletedBy,
        //     }
        //     const promises = [tx.mutate.activeSubstanceCategories.update(updatedRelation)]
        //     if (tx.location === 'server') {
        //       const patches = createPatch({}, updatedRelation)
        //       promises.push(
        //         addEventLog(tx, {
        //           id: uuidv7(),
        //           resource: EventLogResources.activeSubstanceCategoryRelation,
        //           resourceId: updatedRelation.id,
        //           action: 'delete',
        //           patches: patches,
        //           performedBy: updatedRelation.deletedBy,
        //           performedAt: updatedRelation.deletedAt,
        //         })
        //       )
        //     }
        //     return promises
        //   })
        // )
    }),
};
//# sourceMappingURL=active-substance.mutators.js.map